import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionsCart, selectorsCart } from '../../redux/cart/cartReducer';
import CartItem from './CartItem';
import { useFormattingContext } from '../../context/FormattingContext';
import { selectorsOrder } from '../../redux/order/orderReducer';
import { actionsUser } from '../../redux/user/userReducer';
import { useLocation } from 'react-router';
import { isBrowser } from '../../utils/isBrowser';
import { ID_UNIT_WEIGHT } from '../../constants/constantsId';
import { selectorsLocale } from '../../redux/locale/localeReducer';
import { buildLocale } from '../../utils/buildLocale';
import service from '../../services/service';
import { useLessThen991 } from '../../utils/mediaQuery';
import ClipLoader from 'react-spinners/ClipLoader';
import { blockBodyScroll } from '../../utils/blockBodyScroll';
import { setLoaderColor } from '../../utils/setLoaderColor';
import { actionsOther, selectorsOther } from '../../redux/other/otherReducer';
import ShipmentModal from '../Modals/ShipmentModal';
import { selectorsDelivery } from '../../redux/delivery/deliveryReducer';
import { formatWeightWithType, SmallUnits } from '../../utils/calculateUnits';
import { EventSourcesData } from '../../utils/DataLayer/constant';
import ProgressBarComponent from '../common/ProgressBarComponent';
import AnimatedCounter from '../common/AnimatedCounter';

interface IProps {
  sumWithCoupon?: number | null;
  isHeader?: boolean;
  isLowerThenMin?: boolean;
  minPrice?: number;
  isOpenCart?: boolean;
}

const Cart = React.memo((props: IProps) => {
  const { sumWithCoupon, isHeader, isLowerThenMin, minPrice, isOpenCart } = props;
  const location = useLocation();
  const currentTranslate = useSelector(selectorsLocale.getTranslate);
  const cart = useSelector(selectorsCart.getCart);
  const dispatch = useDispatch<any>();
  const { formatPrice } = useFormattingContext();
  const isEditable = useSelector(selectorsOrder.getIsEditable);
  const sum = useSelector(selectorsCart.getGeneralSum);
  const idSellPoint = useSelector(selectorsCart.getIdSellPoint);
  const items = useSelector(selectorsCart.getCartDataProducts);
  const notAvailableProducts = useSelector(selectorsCart.getNotAvailableProducts);
  const isLoadingData = useSelector(selectorsCart.isLoadingData);
  const freeDeliveryOrderAmount = useSelector(selectorsOther.getFreeDeliveryOrderAmount);
  const [snapShot, setSnapshot] = useState({
    sum: 0,
    items: [] as any,
    idSellPoint: null as null | number,
  });
  const cartItems = items && items.length > 0 ? items : snapShot.items;
  const isDesktop = useSelector(selectorsOther.isDesktop);
  const isLess991 = useLessThen991(isDesktop);
  const productsPrice = cart?.productsPrice ? +cart.productsPrice : 0;
  const bottleTotalDeposit = cart?.bottleTotalDeposit ? +cart.bottleTotalDeposit : 0;
  const rackTotalDeposit = cart?.rackTotalDeposit ? +cart.rackTotalDeposit : 0;
  const totalWeightDeposit = cart?.totalWeightDeposit ? +cart.totalWeightDeposit : 0;
  const totalWeight = cart?.totalWeight ? +cart.totalWeight : 0;
  const deliveryCost = cart?.deliveryCost ? +cart.deliveryCost : 0;

  const isInCourierArea = useSelector(selectorsDelivery.isInCourierArea);
  const priceDiffForCheaperDelivery = useSelector(selectorsDelivery.getPriceDiffForCheaperDelivery(productsPrice));
  const currentDeliveryCostForPriceData = useSelector(
    selectorsDelivery.getCurrentDeliveryCostDataForPrice(productsPrice),
  );
  const prevDeliveryCostForPrice = useSelector(selectorsDelivery.getPrevDeliveryCostForPrice(productsPrice));
  const [isOpenShipmentModal, setIsOpenShipmentModal] = useState(false);

  const [deliveryCostStartValue, setDeliveryCostStartValue] = useState(0);
  const [deliveryCostEndValue, setDeliveryCostEndValue] = useState(0);

  const MAX_BOX_WEIGHT = 30000;

  const getDeliveryCostStartValue = () => {
    if (priceDiffForCheaperDelivery === null) {
      return prevDeliveryCostForPrice || 0;
    }

    if (isInCourierArea) {
      return deliveryCost;
    }

    return currentDeliveryCostForPriceData !== null ? currentDeliveryCostForPriceData.price || 0 : 0;
  };

  const getDeliveryCostEndValue = () => {
    if (priceDiffForCheaperDelivery === null) {
      return currentDeliveryCostForPriceData !== null ? currentDeliveryCostForPriceData.price || 0 : 0;
    }

    return priceDiffForCheaperDelivery?.nextDeliveryCost || 0;
  };

  const getFreeBoxDeliveryMessage = () => {
    if (isInCourierArea) {
      return null;
    }

    if (priceDiffForCheaperDelivery === null) {
      return (currentDeliveryCostForPriceData?.price || 0) === getDeliveryCostStartValue() &&
        currentDeliveryCostForPriceData?.hasFreeBox === true
        ? ` <b>${buildLocale(currentTranslate, 'cartOneFreeBoxMessage')}</b>`
        : null;
    }

    return (priceDiffForCheaperDelivery?.nextDeliveryCost || 0) === getDeliveryCostStartValue() &&
      priceDiffForCheaperDelivery?.hasFreeBox === true
      ? ` <b>${buildLocale(currentTranslate, 'cartOneFreeBoxMessage')}</b>`
      : null;
  };

  const freeBoxDeliveryMessage = getFreeBoxDeliveryMessage();
  const currentBoxCount = !isInCourierArea ? Math.ceil(totalWeight / MAX_BOX_WEIGHT) : 0;
  const prevBoxCount = currentBoxCount > 0 ? currentBoxCount - 1 : currentBoxCount;
  const prevFilledBoxWeight = prevBoxCount * MAX_BOX_WEIGHT;
  const currentPartiallyFilledBoxWeight = totalWeight - prevFilledBoxWeight;

  const getTitleBlockSize = (title: string) => {
    if (isBrowser) {
      const infoBlockItems = document.getElementsByClassName('title-name-cart');
      if (infoBlockItems && infoBlockItems.length > 0) {
        for (let i = 0; i < infoBlockItems.length; i++) {
          const slicedTitle = Math.round(infoBlockItems[i].clientWidth / 6.85 - 4);
          return slicedTitle < title.length ? `${title.slice(0, slicedTitle)}...` : title;
        }
      }
    }
    return title;
  };

  useEffect(() => {
    if (window && window.screen.width < 991 && isOpenCart) {
      blockBodyScroll(location && !location.pathname.includes('basket'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenCart]);

  useEffect(() => {
    if (freeDeliveryOrderAmount === null) {
      service
        .calculateDeliveryPrice(1, '1')
        .then((value: any) => {
          dispatch(actionsOther.setFreeDeliveryOrderAmount(value.data.freeDelivery.amountFrom));
        })
        .catch((e) => console.log(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditable) {
      setSnapshot({
        sum,
        items,
        idSellPoint,
      });
      dispatch(actionsCart.clear());
      dispatch(actionsUser.setError(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable]);

  const isWeightUnit = useMemo(() => {
    return items && items.some((el) => el.product.units === ID_UNIT_WEIGHT);
  }, [items]);

  if (isLess991 && isOpenCart === false) {
    return null;
  }

  return (
    <div>
      {isLoadingData && (
        <div className="pay-loader">
          <ClipLoader size={30} color={setLoaderColor()} loading={true} />
        </div>
      )}
      {!isHeader && <p className="basket-block_time">{buildLocale(currentTranslate, 'tenMinMessage')}</p>}
      <div className="basket-block_scroll scrollbar-inner">
        {cartItems.length > 0 &&
          cartItems.map((item, index: number) => {
            const isNotAvailable =
              notAvailableProducts && notAvailableProducts.find((p) => p.product.id === item.product.id);

            const setAvailableType = () => {
              if (!isNotAvailable) {
                return false;
              } else if (isNotAvailable && isNotAvailable.count === 1) {
                return 'noProduct';
              } else {
                return 'count';
              }
            };
            return (
              <CartItem
                key={item.product.id}
                item={item}
                defaultSellPoint={snapShot.idSellPoint}
                notAvailableType={setAvailableType()}
                getTitleBlockSize={getTitleBlockSize}
                dlEcommerceEventSource={EventSourcesData.SHOPPING_CART_PREVIEW}
                dlItemIndex={index}
              />
            );
          })}
        {isWeightUnit && isBrowser && (
          <div className="basket-content_add red-frame red-frame_basket">
            <p className="caption">{buildLocale(currentTranslate, 'commonAttanion')}</p>
            {buildLocale(currentTranslate, 'commonTextWeight')}
          </div>
        )}
      </div>
      <div className="basket-block_bottom">
        {isLowerThenMin && minPrice && (
          <span className="low-price-error">
            {buildLocale(currentTranslate, 'toLowOrderSum')} {formatPrice(minPrice)}
          </span>
        )}
        {bottleTotalDeposit > 0 && (
          <p className="basket-block_bottle">
            <span>{buildLocale(currentTranslate, 'cartTotalBottleDeposit')}</span>
            <span>{formatPrice(bottleTotalDeposit)}</span>
          </p>
        )}
        {rackTotalDeposit > 0 && (
          <p className="basket-block_bottle">
            <span>{buildLocale(currentTranslate, 'cartTotalRackDeposit')}</span>
            <span>{formatPrice(rackTotalDeposit)}</span>
          </p>
        )}
        <p className="basket-block_delivery shipment" onClick={() => setIsOpenShipmentModal(true)}>
          <span>{buildLocale(currentTranslate, 'btnDelivery')}</span>
          <span>{formatPrice(deliveryCost)}</span>
        </p>
        <>
          {!!deliveryCost && !!priceDiffForCheaperDelivery && (
            <p
              className={`basket-block_delivery spend-more ${!!freeBoxDeliveryMessage?.length ? 'is-free-box-delivery' : ''}`}
              dangerouslySetInnerHTML={{
                __html: `${buildLocale(currentTranslate, 'spendMoreAndGetCheaperDelivery', {
                  '{{AMOUNT}}': formatPrice(priceDiffForCheaperDelivery.priceDiff, false),
                })}${!!freeBoxDeliveryMessage?.length ? freeBoxDeliveryMessage : ''}`,
              }}></p>
          )}
          <ProgressBarComponent
            wrapperClassName="basket-progress-bar"
            startProgressElement={
              <span className="endpoint-progress-label">
                <>
                  <AnimatedCounter
                    newValue={getDeliveryCostStartValue()}
                    prevValue={deliveryCostStartValue}
                    updatedValue={deliveryCostStartValue}
                    updateValue={setDeliveryCostStartValue}
                  />
                  {!isInCourierArea && (
                    <span className="not-in-courier-area">
                      /<i className="fa fa-archive"></i>
                    </span>
                  )}
                </>
              </span>
            }
            endProgressElement={
              <span className="endpoint-progress-label endpoint-end">
                <>
                  <AnimatedCounter
                    newValue={getDeliveryCostEndValue()}
                    prevValue={deliveryCostEndValue}
                    updatedValue={deliveryCostEndValue}
                    updateValue={setDeliveryCostEndValue}
                  />
                  {!isInCourierArea && (
                    <>
                      <span className="not-in-courier-area">
                        /<i className="fa fa-archive"></i>
                      </span>
                      {!!freeBoxDeliveryMessage?.length && (
                        <span className="not-in-courier-area">
                          +<i className="fa fa-gift"></i>
                        </span>
                      )}
                    </>
                  )}
                </>
              </span>
            }
            completedClassName="progress-bar progress-bar-striped label-end"
            labelSize="1.4rem"
            isLabelVisible={true}
            customLabel={`<i class="fa fa-truck fa-flip-horizontal"></i>`}
            completed={productsPrice}
            maxCompleted={productsPrice + (priceDiffForCheaperDelivery?.priceDiff || 0)}
          />
        </>
        {!isInCourierArea && !!cart?.totalWeight && (
          <>
            <p className="basket-block_delivery order-weight">
              <span>{buildLocale(currentTranslate, 'weightOfYourShoppingCart')}:</span>
              <span>
                <span>{formatWeightWithType(SmallUnits.g, '', String(totalWeight))}</span>
                <span className="order-weight-package">
                  {currentBoxCount} x <span>{buildLocale(currentTranslate, 'commonPacket')}</span>
                </span>
              </span>
            </p>
            <ProgressBarComponent
              wrapperClassName="basket-progress-bar"
              startProgressElement={
                <span className="endpoint-progress-label bg-revert">
                  <>
                    {currentBoxCount}
                    <span className="not-in-courier-area">
                      x<i className="fa fa-archive"></i>
                    </span>
                  </>
                </span>
              }
              endProgressElement={
                <span className="endpoint-progress-label endpoint-end bg-revert">
                  <>
                    {currentBoxCount + 1}
                    <span className="not-in-courier-area">
                      x<i className="fa fa-archive"></i>
                    </span>
                  </>
                </span>
              }
              completedClassName="progress-bar progress-bar-striped label-end bg-revert"
              customLabel={`<i class="fa fa-truck fa-flip-horizontal"></i>`}
              completed={currentPartiallyFilledBoxWeight}
              maxCompleted={MAX_BOX_WEIGHT}
              baseBgColor={'rgba(11, 74, 135, 0.4)'}
            />
          </>
        )}
        {isOpenShipmentModal && (
          <ShipmentModal isOpen={isOpenShipmentModal} closeModal={() => setIsOpenShipmentModal(false)} />
        )}
        {totalWeightDeposit > 0 && (
          <p className="basket-block_delivery">
            <span>{buildLocale(currentTranslate, 'cartTotalWeightDeposit')}</span>
            <span>{formatPrice(totalWeightDeposit)}</span>
          </p>
        )}
        <p className="basket-block_price">
          <span className="caption">{buildLocale(currentTranslate, 'cartSum')}</span>
          <span className="total-price">
            <span className={`number ${sumWithCoupon ? 'old' : ''}`}>{formatPrice(cart.total)}</span>
          </span>
        </p>
      </div>
    </div>
  );
});

export default Cart;
